/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.initVirtueWidgetStabiliser();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.reserveVirtueWidgetSpace();
      this.prepareVirtueWidget();
    });

    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.reserveVirtueWidgetSpace();
      this.prepareVirtueWidget();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

    if (this.virtueWidgetObserver) {
      this.virtueWidgetObserver.disconnect();
    }

    clearTimeout(this.virtueRevealTimeoutId);
    clearTimeout(this.virtueFailureTimeoutId);
  }

  getVirtueWidget() {
    return this.querySelector(
      '#customer_donations_cart_drawer_widget, '
      + 'iframe[src*="virtueimpact.com"][src*="customer-donations-cart-drawer-widget"]'
    );
  }

  getVirtueHost() {
    return this.querySelector('.checkout-buttons');
  }

  reserveVirtueWidgetSpace() {
    const host = this.getVirtueHost();
    if (!host || host.classList.contains('hidden')) return;

    host.classList.add('virtue-widget-host');

    let storedHeight = 0;
    try {
      storedHeight = parseFloat(sessionStorage.getItem('peatysVirtueCartHostHeight')) || 0;
    } catch (error) {
      storedHeight = 0;
    }

    if (storedHeight > 0) {
      host.style.setProperty('--virtue-cart-host-height', `${storedHeight}px`);
    }

    if (!this.getVirtueWidget()) {
      host.classList.add('virtue-widget-pending');
    }

    clearTimeout(this.virtueFailureTimeoutId);
    this.virtueFailureTimeoutId = setTimeout(() => {
      if (!this.getVirtueWidget()) {
        host.classList.remove('virtue-widget-pending', 'virtue-widget-host');
        host.style.removeProperty('--virtue-cart-host-height');
      }
    }, 4000);
  }

  initVirtueWidgetStabiliser() {
    this.reserveVirtueWidgetSpace();
    this.prepareVirtueWidget();

    if (!('MutationObserver' in window)) return;

    this.virtueWidgetObserver = new MutationObserver((mutations) => {
      const changed = mutations.some((mutation) => (
        mutation.type === 'childList'
        && (
          mutation.addedNodes.length > 0
          || mutation.removedNodes.length > 0
        )
      ));

      if (changed) {
        this.reserveVirtueWidgetSpace();
        this.prepareVirtueWidget();
      }
    });

    this.virtueWidgetObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  prepareVirtueWidget() {
    const iframe = this.getVirtueWidget();
    const host = this.getVirtueHost();

    if (!iframe || !host) return;

    clearTimeout(this.virtueFailureTimeoutId);

    host.classList.add('virtue-widget-host');
    host.classList.remove('virtue-widget-pending');

    if (this.currentVirtueWidget === iframe) {
      return;
    }

    this.currentVirtueWidget = iframe;

    if (this.virtueHasRevealed) {
      iframe.classList.add('virtue-widget-ready');
      iframe.classList.remove('virtue-widget-stabilising');
      this.rememberVirtueHostHeight();
      return;
    }

    iframe.classList.add('virtue-widget-stabilising');
    iframe.classList.remove('virtue-widget-ready');

    iframe.addEventListener('load', () => {
      if (!this.virtueHasRevealed) {
        this.scheduleVirtueWidgetReveal(iframe);
      }
    });

    this.scheduleVirtueWidgetReveal(iframe);
  }

  scheduleVirtueWidgetReveal(iframe) {
    if (this.virtueHasRevealed) return;

    clearTimeout(this.virtueRevealTimeoutId);

    this.virtueRevealTimeoutId = setTimeout(() => {
      const currentIframe = this.getVirtueWidget();
      if (!iframe.isConnected || iframe !== currentIframe) return;

      this.virtueHasRevealed = true;
      iframe.classList.remove('virtue-widget-stabilising');
      iframe.classList.add('virtue-widget-ready');

      this.rememberVirtueHostHeight();
    }, 300);
  }

  rememberVirtueHostHeight() {
    const host = this.getVirtueHost();
    if (!host) return;

    requestAnimationFrame(() => {
      const height = Math.ceil(host.getBoundingClientRect().height);
      if (height <= 0) return;

      host.style.setProperty('--virtue-cart-host-height', `${height}px`);

      try {
        sessionStorage.setItem('peatysVirtueCartHostHeight', String(height));
      } catch (error) {
        // Storage may be unavailable in privacy modes.
      }
    });
  }

  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);
